<template>
  <div class="landing-wrapper">
    <v-container class="py-8 py-md-12" fluid>
      <v-row justify="center">
        <v-col cols="12" lg="10">
          <v-alert v-if="error" type="error" class="mb-4" closable @click:close="error = ''">
            {{ error }}
          </v-alert>

          <div class="rp-panel">
            <div class="rp-panel-header">
              <v-icon size="22" color="#ffffff">mdi-file-search-outline</v-icon>
              <span>ADMINISTRACIÓN / INFORMES</span>
            </div>

            <div class="rp-panel-body">
              <!-- Buscador: texto libre con sugerencias + tipo de informe -->
              <p class="rp-label">BÚSQUEDA DE INFORMES</p>
              <form class="rp-search" @submit.prevent="load">
                <input
                  v-model="search"
                  type="search"
                  list="rp-suggestions"
                  class="rp-input"
                  placeholder="BUSQUE POR COMPAÑÍA DE TEATRO, CLIENTE U OBRA DE TEATRO"
                />
                <datalist id="rp-suggestions">
                  <option v-for="s in suggestions" :key="s" :value="s" />
                </datalist>

                <select v-model="tipo" class="rp-select" @change="load">
                  <option value="hitos">HITOS</option>
                  <option value="contratos">CONTRATOS</option>
                </select>

                <button type="submit" class="rp-search-btn" aria-label="Buscar">
                  <v-icon size="20" color="#ffffff">mdi-magnify</v-icon>
                </button>
              </form>

              <div class="rp-results-head">
                <h3 class="rp-results-title">RESULTADOS</h3>
                <span v-if="!loading" class="rp-results-count">
                  {{ count }} {{ count === 1 ? 'RESERVA' : 'RESERVAS' }}
                </span>
              </div>

              <p v-if="truncated" class="rp-truncated">
                Mostrando las {{ rows.length }} más recientes de {{ count }}. Afina la
                búsqueda para ver el resto.
              </p>

              <div v-if="loading" class="d-flex justify-center py-8">
                <v-progress-circular indeterminate color="primary" size="40" />
              </div>

              <div v-else class="rp-table-scroll">
                <table class="rp-table">
                  <thead>
                    <tr>
                      <th>OBRA</th>
                      <th>FECHA FUNCIÓN</th>
                      <th>CLIENTE</th>
                      <th>COMPAÑÍA DE TEATRO</th>
                      <th>{{ tipo === 'contratos' ? 'CONTRATO' : 'ESTADO DE LA RESERVA' }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="r in rows"
                      :key="r.id"
                      class="rp-row"
                      @click="goToReservation(r)"
                    >
                      <td class="rp-cell-name">{{ r.play_title }}</td>
                      <td>{{ formatDate(r.function_date) }}</td>
                      <td>{{ r.client_name || '—' }}</td>
                      <td>{{ r.company_name || '—' }}</td>
                      <td>
                        <a
                          v-if="tipo === 'contratos' && r.contract_url"
                          class="rp-doc-link"
                          :href="r.contract_url"
                          target="_blank"
                          rel="noopener"
                          @click.stop
                        >
                          <v-icon size="14">mdi-file-document-outline</v-icon>
                          VER CONTRATO
                        </a>
                        <span v-else class="rp-status">
                          <span class="rp-dot" :style="dotStyle(r.status)"></span>
                          {{ statusLabel(r.status) }}
                        </span>
                      </td>
                    </tr>
                    <tr v-if="rows.length === 0">
                      <td colspan="5" class="rp-empty">
                        {{ emptyMessage }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <teatro-footer />
  </div>
</template>

<script>
import { fetchReports, fetchReportOptions } from "@/services/teatroApi";
import { statusInfo } from "./reservationStatus";

export default {
  name: "ReportSearch",
  data() {
    return {
      search: "",
      tipo: "hitos",
      rows: [],
      count: 0,
      truncated: false,
      suggestions: [],
      loading: true,
      error: "",
    };
  },
  computed: {
    emptyMessage() {
      if (this.search) return "Ningún resultado para esa búsqueda.";
      if (this.tipo === "contratos") return "Todavía no hay contratos cargados.";
      return "Todavía no hay reservas registradas.";
    },
  },
  async created() {
    await this.loadAuth();
    await Promise.all([this.loadOptions(), this.load()]);
  },
  methods: {
    async loadAuth() {
      try {
        await this.$store.dispatch("fetchIsAuthenticated");
      } catch (e) {
        console.error(e);
      }
    },
    async loadOptions() {
      try {
        const o = await fetchReportOptions();
        // Una sola lista para el datalist: obras, compañías y clientes.
        this.suggestions = [
          ...(o.plays || []),
          ...(o.companies || []),
          ...(o.clients || []),
        ];
      } catch (e) {
        console.error(e);
      }
    },
    async load() {
      this.loading = true;
      this.error = "";
      try {
        const data = await fetchReports(this.search, this.tipo);
        this.rows = data.results || [];
        this.count = data.count || 0;
        this.truncated = data.truncated === true;
      } catch (e) {
        this.error = "No se pudo cargar el informe.";
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    statusLabel(status) {
      return statusInfo(status).label.toUpperCase();
    },
    // Mismo código de color que la línea de tiempo: relleno o sólo borde.
    dotStyle(status) {
      const info = statusInfo(status);
      return info.fill
        ? { background: info.color, borderColor: info.color }
        : { background: "transparent", borderColor: info.color };
    },
    formatDate(iso) {
      if (!iso) return "—";
      const [y, m, d] = String(iso).slice(0, 10).split("-");
      return `${d}/${m}/${y}`;
    },
    goToReservation(r) {
      window.location.href = `/reserva/${r.id}/`;
    },
  },
};
</script>

<style scoped>
.rp-panel {
  max-width: 1080px;
  margin: 0 auto;
  background: rgba(8, 5, 5, 0.66);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 26px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
}

.rp-panel-header {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 66px;
  padding: 0 32px;
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
  font-family: var(--font-family-heading);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 1.4px;
}

.rp-panel-body {
  padding: 26px 34px 30px;
}

.rp-label {
  color: var(--color-theater-text-muted);
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  margin: 0 0 10px;
}

.rp-search {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 30px;
}

.rp-input {
  flex: 1 1 380px;
  min-width: 240px;
  padding: 12px 22px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 999px;
  outline: none;
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-size: 12px;
  letter-spacing: 0.5px;
}

.rp-input::placeholder {
  color: rgba(255, 255, 255, 0.42);
}

.rp-input:focus {
  border-color: rgba(255, 255, 255, 0.4);
}

.rp-select {
  padding: 12px 18px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 999px;
  outline: none;
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  cursor: pointer;
}

.rp-select option {
  color: #1a1a1a;
}

.rp-search-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border: none;
  border-radius: 50%;
  background: #0070b9;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.rp-search-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(0, 112, 185, 0.45);
}

.rp-results-head {
  display: flex;
  align-items: baseline;
  gap: 14px;
  margin-bottom: 6px;
}

.rp-results-title {
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-size: 20px;
  font-weight: 800;
  letter-spacing: 1.2px;
  margin: 0;
}

.rp-results-count {
  color: var(--color-theater-text-muted);
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
}

.rp-truncated {
  color: #f0b64b;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 600;
  margin: 0 0 8px;
}

.rp-table-scroll {
  overflow-x: auto;
  margin-top: 12px;
}

.rp-table {
  width: 100%;
  border-collapse: collapse;
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
}

.rp-table th {
  text-align: left;
  padding: 8px 16px 18px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.55);
  white-space: nowrap;
}

.rp-table td {
  padding: 15px 16px;
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.4px;
  vertical-align: middle;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.rp-table tbody tr:first-child td {
  border-top: none;
}

.rp-cell-name {
  text-transform: uppercase;
}

.rp-row {
  cursor: pointer;
  transition: background 0.15s ease;
}

.rp-row:hover {
  background: rgba(255, 255, 255, 0.05);
}

.rp-status {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  white-space: nowrap;
}

.rp-dot {
  width: 13px;
  height: 13px;
  border-radius: 50%;
  border: 2px solid transparent;
  flex: 0 0 auto;
}

.rp-doc-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #6fb7ea;
  text-decoration: none;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.6px;
  white-space: nowrap;
}

.rp-doc-link:hover {
  text-decoration: underline;
}

.rp-empty {
  text-align: center;
  padding: 30px 16px;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
}
</style>
